import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { bedService, complaintService, floorService, roomService } from '../services/api';
import { Bed, Complaint, Floor, Room } from '../types';
import { formatFloorShortLabel } from '../utils/locationLabels';
import PhoneLink from '../components/PhoneLink';
import TenantNameLink from '../components/TenantNameLink';
import './ComplaintManagement.css';

type ComplaintStatus = 'NEW' | 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CANCELLED';

const STATUS_OPTIONS: { value: ComplaintStatus; label: string; rowClass: string }[] = [
  { value: 'NEW', label: 'New', rowClass: 'complaints-status-bar-row--new' },
  { value: 'OPEN', label: 'Open', rowClass: 'complaints-status-bar-row--open' },
  { value: 'IN_PROGRESS', label: 'In progress', rowClass: 'complaints-status-bar-row--in_progress' },
  { value: 'RESOLVED', label: 'Resolved', rowClass: 'complaints-status-bar-row--resolved' },
  { value: 'CANCELLED', label: 'Cancelled', rowClass: 'complaints-status-bar-row--cancelled' },
];

const CATEGORIES = [
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'CLEANLINESS', label: 'Cleanliness' },
  { value: 'NOISE', label: 'Noise' },
  { value: 'AMENITIES', label: 'Amenities' },
  { value: 'STAFF', label: 'Staff' },
  { value: 'OTHERS', label: 'Others' },
];

function normalizeStatus(status?: string): ComplaintStatus | 'CLOSED' {
  const s = String(status ?? 'NEW').trim().toUpperCase();
  if (s === 'CLOSED') return 'CANCELLED';
  if (STATUS_OPTIONS.some((o) => o.value === s)) return s as ComplaintStatus;
  return 'NEW';
}

function statusLabel(status?: string): string {
  const key = normalizeStatus(status);
  return STATUS_OPTIONS.find((o) => o.value === key)?.label || key;
}

function categoryLabel(cat?: string): string {
  return CATEGORIES.find((c) => c.value === cat)?.label || cat || '—';
}

function formatDate(val?: string): string {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(val?: string): string {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type LocationForm = { floorId: number | ''; roomId: number | ''; bedId: number | '' };

const emptyLocationForm = (): LocationForm => ({ floorId: '', roomId: '', bedId: '' });

const formatFloorLabel = formatFloorShortLabel;

function formatComplaintLocation(c: Complaint): string {
  const parts: string[] = [];
  if (c.floor) {
    parts.push(formatFloorLabel(c.floor as Floor));
  }
  if (c.room) {
    parts.push(`Room ${c.room.roomNumber}`);
  }
  if (c.bed) {
    parts.push(`Bed ${c.bed.bedNumber}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function getComplaintPhotoUrl(complaint: Complaint): string | null {
  const att = complaint.attachments;
  if (!att || !Array.isArray(att) || att.length === 0) return null;
  const first = att[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'url' in first && first.url) return String(first.url);
  return null;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function currentMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toISODate(start), end: toISODate(end) };
}

const ComplaintManagement: React.FC = () => {
  const { hostel, user } = useAuth();
  const monthRange = currentMonthRange();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [rangeStart, setRangeStart] = useState(monthRange.start);
  const [rangeEnd, setRangeEnd] = useState(monthRange.end);
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ComplaintStatus | 'ALL'>('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [expandedPhotoUrl, setExpandedPhotoUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [layoutFloors, setLayoutFloors] = useState<Floor[]>([]);
  const [layoutRooms, setLayoutRooms] = useState<Room[]>([]);
  const [roomBeds, setRoomBeds] = useState<Bed[]>([]);
  const [locationForm, setLocationForm] = useState<LocationForm>(emptyLocationForm);
  const [newComplaint, setNewComplaint] = useState({
    title: '',
    description: '',
    category: 'MAINTENANCE',
    priority: 'MEDIUM' as Complaint['priority'],
  });

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const closeCreateModal = () => {
    if (submitting) return;
    clearPhoto();
    setLocationForm(emptyLocationForm());
    setRoomBeds([]);
    setShowCreate(false);
  };

  const roomsOnFloor = useMemo(() => {
    if (locationForm.floorId === '') return [];
    const fid = Number(locationForm.floorId);
    return layoutRooms.filter((r) => Number(r.floorId) === fid);
  }, [layoutRooms, locationForm.floorId]);

  useEffect(() => {
    if (!showCreate || !hostel?.id) return;
    let cancelled = false;
    Promise.all([floorService.getFloorsByHostel(hostel.id), roomService.getRoomsByHostel(hostel.id)])
      .then(([floors, rooms]) => {
        if (cancelled) return;
        setLayoutFloors(floors.filter((f) => f.isActive !== false));
        setLayoutRooms(rooms.filter((r) => r.isActive !== false));
      })
      .catch(() => toast.error('Failed to load floors and rooms'));
    return () => {
      cancelled = true;
    };
  }, [showCreate, hostel?.id]);

  useEffect(() => {
    if (!showCreate || locationForm.roomId === '') {
      setRoomBeds([]);
      return;
    }
    const rid = Number(locationForm.roomId);
    if (!Number.isFinite(rid)) return;
    let cancelled = false;
    bedService
      .getBedsByRoom(rid)
      .then((beds) => {
        if (!cancelled) setRoomBeds(beds.filter((b) => b.isActive !== false));
      })
      .catch(() => {
        if (!cancelled) {
          setRoomBeds([]);
          toast.error('Could not load beds for room');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showCreate, locationForm.roomId]);

  const loadComplaints = useCallback(async () => {
    if (!hostel?.id) return;
    try {
      setLoading(true);
      const data = await complaintService.getComplaintsByHostel(hostel.id);
      setComplaints(data);
    } catch {
      toast.error('Failed to load complaints');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  }, [hostel?.id]);

  useEffect(() => {
    void loadComplaints();
  }, [loadComplaints]);

  useEffect(() => {
    if (!expandedPhotoUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedPhotoUrl(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedPhotoUrl]);

  useEffect(() => {
    setPage(1);
  }, [query, rangeStart, rangeEnd, statusFilter, priorityFilter, pageSize]);

  const inDateRange = useCallback(
    (c: Complaint) => {
      if (!rangeStart && !rangeEnd) return true;
      const raw = c.createdAt || c.updatedAt;
      if (!raw) return true;
      const t = new Date(raw).getTime();
      if (!Number.isFinite(t)) return true;
      if (rangeStart) {
        const s = new Date(rangeStart);
        s.setHours(0, 0, 0, 0);
        if (t < s.getTime()) return false;
      }
      if (rangeEnd) {
        const e = new Date(rangeEnd);
        e.setHours(23, 59, 59, 999);
        if (t > e.getTime()) return false;
      }
      return true;
    },
    [rangeStart, rangeEnd],
  );

  const periodComplaints = useMemo(
    () => complaints.filter(inDateRange),
    [complaints, inDateRange],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<ComplaintStatus, number> = {
      NEW: 0,
      OPEN: 0,
      IN_PROGRESS: 0,
      RESOLVED: 0,
      CANCELLED: 0,
    };
    periodComplaints.forEach((c) => {
      const key = normalizeStatus(c.status) as ComplaintStatus;
      counts[key] += 1;
    });
    return counts;
  }, [periodComplaints]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return periodComplaints
      .filter((c) => {
        if (statusFilter !== 'ALL' && normalizeStatus(c.status) !== statusFilter) return false;
        if (priorityFilter !== 'ALL' && c.priority !== priorityFilter) return false;
        if (!q) return true;
        const hay = [c.title, c.description, c.tenant?.username, c.tenant?.phoneNumber, categoryLabel(c.category)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
  }, [periodComplaints, query, statusFilter, priorityFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const pageNumbers = useMemo(() => {
    const max = Math.min(totalPages, 10);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [totalPages]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id || !user?.id) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('hostelId', String(hostel.id));
      formData.append('title', newComplaint.title);
      formData.append('description', newComplaint.description);
      formData.append('category', newComplaint.category);
      formData.append('priority', newComplaint.priority || 'MEDIUM');
      if (photoFile) formData.append('photo', photoFile);
      if (locationForm.floorId !== '') formData.append('floorId', String(locationForm.floorId));
      if (locationForm.roomId !== '') formData.append('roomId', String(locationForm.roomId));
      if (locationForm.bedId !== '') formData.append('bedId', String(locationForm.bedId));

      await complaintService.createComplaint(formData);
      toast.success('Complaint created');
      setNewComplaint({ title: '', description: '', category: 'MAINTENANCE', priority: 'MEDIUM' });
      setLocationForm(emptyLocationForm());
      setRoomBeds([]);
      clearPhoto();
      setShowCreate(false);
      await loadComplaints();
    } catch (err: unknown) {
      const er = err as { response?: { data?: { message?: string } } };
      toast.error(er?.response?.data?.message || 'Failed to create complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (id: number, status: ComplaintStatus) => {
    setStatusUpdatingId(id);
    try {
      await complaintService.updateComplaintStatus(id, status);
      setComplaints((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  if (!hostel) {
    return <div className="complaints-loading">Loading…</div>;
  }

  return (
    <div className="complaints-page">
      <header className="complaints-page-header">
        <h1 className="complaints-page-title">Complaints</h1>
        <button type="button" className="complaints-btn complaints-btn--primary" onClick={() => setShowCreate(true)}>
          + New Complaint
        </button>
      </header>

      <div className="complaints-toolbar">
        <label className="complaints-search-wrap">
          <span aria-hidden>🔍</span>
          <input
            type="search"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search complaints"
          />
        </label>
        <div className="complaints-date-wrap">
          <span aria-hidden>📅</span>
          <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} aria-label="From date" />
          <span>–</span>
          <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} aria-label="To date" />
        </div>
        <button
          type="button"
          className={`complaints-btn complaints-filter-btn${filterOpen ? ' is-active' : ''}`}
          onClick={() => setFilterOpen((o) => !o)}
          aria-label="Toggle filters"
        >
          ⏷
        </button>
      </div>

      {filterOpen ? (
        <div className="complaints-filter-panel">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ComplaintStatus | 'ALL')}>
            <option value="ALL">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="ALL">All priorities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>
          <button
            type="button"
            className="complaints-btn"
            onClick={() => {
              const r = currentMonthRange();
              setRangeStart(r.start);
              setRangeEnd(r.end);
              setStatusFilter('ALL');
              setPriorityFilter('ALL');
            }}
          >
            This month
          </button>
        </div>
      ) : null}

      <section className="complaints-summary-section" aria-label="Status summary">
        <h2 className="complaints-summary-title">Status wise Summary</h2>
        <div className="complaints-summary-card">
          <div className="complaints-status-bars">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`complaints-status-bar-row ${s.rowClass}${statusFilter === s.value ? ' is-active' : ''}`}
                onClick={() => setStatusFilter((prev) => (prev === s.value ? 'ALL' : s.value))}
              >
                <span className="complaints-status-bar-label">{s.label}</span>
                <span className="complaints-status-bar-track">{statusCounts[s.value]}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="complaints-table-card">
        {loading ? (
          <p className="complaints-loading">Loading complaints…</p>
        ) : (
          <>
            <div className="complaints-table-scroll responsive-table-wrap">
              <table className="complaints-table responsive-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Tenant</th>
                    <th>Location</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 ? (
                    <tr className="responsive-table__empty">
                      <td colSpan={9} className="no-data" data-label="">
                        No complaints found
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((c) => {
                      const norm = normalizeStatus(c.status);
                      const pri = (c.priority || 'MEDIUM').toLowerCase();
                      return (
                        <tr key={c.id}>
                          <td data-label="ID">#{c.id}</td>
                          <td className="responsive-table__primary" data-label="Title">
                            <span className="complaints-title-cell" title={c.title}>
                              {c.title}
                            </span>
                          </td>
                          <td data-label="Tenant">
                            <TenantNameLink
                              userId={c.tenant?.id}
                              name={c.tenant?.username || c.tenant?.phoneNumber}
                            />
                          </td>
                          <td className="complaints-col-location responsive-table__full" data-label="Location" title={formatComplaintLocation(c)}>
                            {formatComplaintLocation(c)}
                          </td>
                          <td data-label="Category">{categoryLabel(c.category)}</td>
                          <td data-label="Priority">
                            <span className={`complaints-priority complaints-priority--${pri}`}>{c.priority}</span>
                          </td>
                          <td data-label="Date">{formatDate(c.createdAt)}</td>
                          <td className="responsive-table__actions" data-label="Status">
                            <select
                              className="complaints-status-select"
                              value={norm}
                              disabled={statusUpdatingId === c.id}
                              onChange={(e) => void handleStatusChange(c.id, e.target.value as ComplaintStatus)}
                              aria-label={`Status for complaint ${c.id}`}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="responsive-table__actions" data-label="Actions">
                            <div className="complaints-actions">
                              <button
                                type="button"
                                className="complaints-action-btn"
                                onClick={() => setSelectedComplaint(c)}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <footer className="complaints-table-footer">
              <div className="complaints-pagination">
                <button
                  type="button"
                  className="complaints-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ←
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`complaints-page-btn${n === page ? ' is-active' : ''}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="complaints-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
              <div className="complaints-footer-meta">
                <span>
                  Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, filtered.length)} of {filtered.length} entries
                </span>
                <label className="complaints-page-size">
                  Show
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                    <option value={8}>8</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>
            </footer>
          </>
        )}
      </section>

      {showCreate ? (
        <div className="complaints-modal-overlay" role="presentation" onClick={closeCreateModal}>
          <div className="complaints-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <header className="complaints-modal-header">
              <h2>New complaint</h2>
              <button type="button" className="complaints-modal-close" onClick={closeCreateModal} disabled={submitting}>
                ×
              </button>
            </header>
            <form onSubmit={handleCreate}>
              <div className="complaints-modal-body">
                <div className="complaints-form-grid">
                  <label className="complaints-form-field complaints-form-field--full">
                    <span>Title</span>
                    <input
                      required
                      value={newComplaint.title}
                      onChange={(e) => setNewComplaint({ ...newComplaint, title: e.target.value })}
                    />
                  </label>
                  <label className="complaints-form-field">
                    <span>Category</span>
                    <select
                      value={newComplaint.category}
                      onChange={(e) => setNewComplaint({ ...newComplaint, category: e.target.value })}
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="complaints-form-field">
                    <span>Priority</span>
                    <select
                      value={newComplaint.priority}
                      onChange={(e) =>
                        setNewComplaint({ ...newComplaint, priority: e.target.value as Complaint['priority'] })
                      }
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </label>
                  <label className="complaints-form-field complaints-form-field--full">
                    <span>Description</span>
                    <textarea
                      required
                      rows={4}
                      value={newComplaint.description}
                      onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                    />
                  </label>
                  <label className="complaints-form-field">
                    <span>Floor (optional)</span>
                    <select
                      value={locationForm.floorId === '' ? '' : String(locationForm.floorId)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLocationForm({
                          floorId: v === '' ? '' : Number(v),
                          roomId: '',
                          bedId: '',
                        });
                      }}
                    >
                      <option value="">— None —</option>
                      {layoutFloors.map((f) => (
                        <option key={f.id} value={f.id}>
                          {formatFloorLabel(f)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="complaints-form-field">
                    <span>Room (optional)</span>
                    <select
                      disabled={locationForm.floorId === ''}
                      value={locationForm.roomId === '' ? '' : String(locationForm.roomId)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLocationForm((prev) => ({
                          ...prev,
                          roomId: v === '' ? '' : Number(v),
                          bedId: '',
                        }));
                      }}
                    >
                      <option value="">— None —</option>
                      {roomsOnFloor.map((r) => (
                        <option key={r.id} value={r.id}>
                          Room {r.roomNumber}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="complaints-form-field">
                    <span>Bed (optional)</span>
                    <select
                      disabled={locationForm.roomId === ''}
                      value={locationForm.bedId === '' ? '' : String(locationForm.bedId)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLocationForm((prev) => ({
                          ...prev,
                          bedId: v === '' ? '' : Number(v),
                        }));
                      }}
                    >
                      <option value="">— None —</option>
                      {roomBeds.map((b) => (
                        <option key={b.id} value={b.id}>
                          Bed {b.bedNumber}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="complaints-form-field complaints-form-field--full">
                    <span>Photo (optional)</span>
                    <div className="complaints-photo-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="complaints-photo-input"
                        id="complaint-photo-input"
                      />
                      <label htmlFor="complaint-photo-input" className="complaints-photo-label">
                        {photoPreview ? 'Change photo' : 'Choose image'}
                      </label>
                      {photoPreview ? (
                        <div className="complaints-photo-preview-wrap">
                          <img src={photoPreview} alt="Complaint preview" className="complaints-photo-preview" />
                          <button type="button" className="complaints-photo-remove" onClick={clearPhoto}>
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </label>
                </div>
                <p className="complaints-form-hint">
                  Status will be set to <strong>New</strong> automatically.
                </p>
              </div>
              <footer className="complaints-modal-footer">
                <button type="button" className="complaints-btn" onClick={closeCreateModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="complaints-btn complaints-btn--primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {selectedComplaint ? (
        <div
          className="complaints-modal-overlay"
          role="presentation"
          onClick={() => {
            setExpandedPhotoUrl(null);
            setSelectedComplaint(null);
          }}
        >
          <div className="complaints-modal complaints-modal--detail" role="dialog" onClick={(e) => e.stopPropagation()}>
            <header className="complaints-modal-header">
              <div>
                <p className="complaints-detail-id">#{selectedComplaint.id}</p>
                <h2>{selectedComplaint.title}</h2>
              </div>
              <button
                type="button"
                className="complaints-modal-close"
                onClick={() => {
                  setExpandedPhotoUrl(null);
                  setSelectedComplaint(null);
                }}
              >
                ×
              </button>
            </header>
            <div className="complaints-modal-body">
              <div className="complaints-detail-grid">
                <div className="complaints-detail-row">
                  <span className="complaints-detail-label">Status</span>
                  <span
                    className={`complaints-detail-badge complaints-detail-badge--${normalizeStatus(selectedComplaint.status).toLowerCase()}`}
                  >
                    {statusLabel(selectedComplaint.status)}
                  </span>
                </div>
                <div className="complaints-detail-row">
                  <span className="complaints-detail-label">Priority</span>
                  <span
                    className={`complaints-priority complaints-priority--${(selectedComplaint.priority || 'MEDIUM').toLowerCase()}`}
                  >
                    {selectedComplaint.priority || 'MEDIUM'}
                  </span>
                </div>
                <div className="complaints-detail-row">
                  <span className="complaints-detail-label">Category</span>
                  <span>{categoryLabel(selectedComplaint.category)}</span>
                </div>
                <div className="complaints-detail-row">
                  <span className="complaints-detail-label">Tenant</span>
                  <span>
                    {selectedComplaint.tenant?.username || '—'}
                    {selectedComplaint.tenant?.phoneNumber ? (
                      <>
                        {' · '}
                        <PhoneLink phone={selectedComplaint.tenant.phoneNumber} />
                      </>
                    ) : null}
                  </span>
                </div>
                {formatComplaintLocation(selectedComplaint) !== '—' ? (
                  <div className="complaints-detail-row complaints-detail-row--full">
                    <span className="complaints-detail-label">Location</span>
                    <span>{formatComplaintLocation(selectedComplaint)}</span>
                  </div>
                ) : null}
                <div className="complaints-detail-row">
                  <span className="complaints-detail-label">Created</span>
                  <span>{formatDateTime(selectedComplaint.createdAt)}</span>
                </div>
                {selectedComplaint.updatedAt ? (
                  <div className="complaints-detail-row">
                    <span className="complaints-detail-label">Updated</span>
                    <span>{formatDateTime(selectedComplaint.updatedAt)}</span>
                  </div>
                ) : null}
                {selectedComplaint.assignedStaff?.username ? (
                  <div className="complaints-detail-row">
                    <span className="complaints-detail-label">Assigned to</span>
                    <span>{selectedComplaint.assignedStaff.username}</span>
                  </div>
                ) : null}
              </div>
              <div className="complaints-detail-row complaints-detail-row--block">
                <span className="complaints-detail-label">Description</span>
                <p className="complaints-detail-description">{selectedComplaint.description || '—'}</p>
              </div>
              {selectedComplaint.resolutionNotes ? (
                <div className="complaints-detail-row complaints-detail-row--block">
                  <span className="complaints-detail-label">Resolution notes</span>
                  <p className="complaints-detail-description">{selectedComplaint.resolutionNotes}</p>
                </div>
              ) : null}
              {getComplaintPhotoUrl(selectedComplaint) ? (
                <div className="complaints-detail-photo">
                  <span className="complaints-detail-label">Photo</span>
                  <button
                    type="button"
                    className="complaints-detail-photo-btn"
                    onClick={() => setExpandedPhotoUrl(getComplaintPhotoUrl(selectedComplaint)!)}
                    aria-label="Expand complaint photo"
                  >
                    <img src={getComplaintPhotoUrl(selectedComplaint)!} alt="Complaint" />
                  </button>
                </div>
              ) : null}
            </div>
            <footer className="complaints-modal-footer">
              <button
                type="button"
                className="complaints-btn complaints-btn--primary"
                onClick={() => {
                  setExpandedPhotoUrl(null);
                  setSelectedComplaint(null);
                }}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {expandedPhotoUrl ? (
        <div
          className="complaints-photo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded complaint photo"
          onClick={() => setExpandedPhotoUrl(null)}
        >
          <button
            type="button"
            className="complaints-photo-lightbox-close"
            onClick={() => setExpandedPhotoUrl(null)}
            aria-label="Close expanded photo"
          >
            ×
          </button>
          <img
            src={expandedPhotoUrl}
            alt="Complaint full size"
            className="complaints-photo-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ComplaintManagement;
